import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { truncateDecimals } from 'src/app/app.utils';
import { DeviationSurveyChartPoint, DeviationSurveyGridRow } from './deviation-survey.interfaces';
import { DEVIATION_SURVEY_CONSTANTS } from './deviation-survey.constants';
import { FormulasService } from './formulas.service';

const KEYS = {
  isShowMarker: 'app.trajectory.isShowMarker',
  isShowRodTapers: 'app.trajectory.isShowRodTapers',
  isTrueScale: 'app.trajectory.isTrueScale',
} as const;

/** Reads a boolean value from localStorage, returning the fallback if absent or unreadable. */
function loadBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as boolean);
  } catch {
    return fallback;
  }
}

/** Persists a boolean value to localStorage, silently ignoring write errors. */
function saveBool(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Best-effort persistence — ignore quota-exceeded or storage-disabled errors.
  }
}

@Injectable({ providedIn: 'root' })
export class DeviationSurveyService {
  // Injected services
  private formulas = inject(FormulasService);
  private toastr = inject(ToastrService);

  // Constants
  readonly DEFAULT_STEP_SIZE = DEVIATION_SURVEY_CONSTANTS.DEFAULT_STEP_SIZE;
  readonly DEFAULT_GREEN_ZONE_LIMIT = DEVIATION_SURVEY_CONSTANTS.DEFAULT_GREEN_ZONE_LIMIT;
  readonly DEFAULT_BLUE_ZONE_LIMIT = DEVIATION_SURVEY_CONSTANTS.DEFAULT_BLUE_ZONE_LIMIT;
  readonly MAX_CHART_POINTS = DEVIATION_SURVEY_CONSTANTS.MAX_CHART_POINTS;
  readonly GREEN_ZONE_COLOR = DEVIATION_SURVEY_CONSTANTS.GREEN_ZONE_COLOR;
  readonly BLUE_ZONE_COLOR = DEVIATION_SURVEY_CONSTANTS.BLUE_ZONE_COLOR;
  readonly RED_ZONE_COLOR = DEVIATION_SURVEY_CONSTANTS.RED_ZONE_COLOR;
  readonly NO_RODS_COLOR = DEVIATION_SURVEY_CONSTANTS.NO_RODS_COLOR;
  readonly columnNames = DEVIATION_SURVEY_CONSTANTS.COLUMN_NAMES;

  // Signals — reactive state consumed by chart computed()s
  readonly isShowMarker = signal(loadBool(KEYS.isShowMarker, true));
  readonly isShowRodTapers = signal(loadBool(KEYS.isShowRodTapers, false));
  readonly isTrueScale = signal(loadBool(KEYS.isTrueScale, false));

  private readonly _persistToggles = effect(() => {
    saveBool(KEYS.isShowMarker, this.isShowMarker());
    saveBool(KEYS.isShowRodTapers, this.isShowRodTapers());
    saveBool(KEYS.isTrueScale, this.isTrueScale());
  });
  readonly pumpDepth = signal(0);
  readonly anchorDepth = signal(0);
  /** Whether the tubing is anchored (well.tubing.isAnchored) — the anchor depth marker only
   *  makes sense to show when there's actually an anchor. */
  readonly isTubingAnchored = signal(false);
  readonly rodZones = signal<{ color: string; value?: number }[]>([]);
  readonly rodTypes = signal<
    { color: string; rodType: string; diameter: number | null; length: number | null }[]
  >([]);
  readonly greenZoneLimit = signal(this.DEFAULT_GREEN_ZONE_LIMIT);
  readonly blueZoneLimit = signal(this.DEFAULT_BLUE_ZONE_LIMIT);
  readonly lastStrokePeakLoad = signal(0);
  readonly maxSideLoad = signal(0);
  readonly chartInfo = signal<DeviationSurveyChartPoint[]>([]);
  // Position-only copies updated exclusively on survey load — not on stroke SL recalculation.
  // The trajectory path reads these so it never re-renders when only SL changes.
  readonly trajectoryChartInfo = signal<DeviationSurveyChartPoint[]>([]);
  readonly trajectoryStepSizedChartInfo = signal<DeviationSurveyChartPoint[]>([]);
  readonly selectedRowNumber = signal<number | null>(null);

  // Plain — not needed for chart reactivity
  isEditing = false;
  stepSize: number = this.DEFAULT_STEP_SIZE;

  // Validation computed properties
  readonly hasErrorInGreenZoneLimit = computed(() => this.greenZoneLimit() <= 0);
  readonly hasErrorInBlueZoneLimit = computed(() => this.blueZoneLimit() <= this.greenZoneLimit());

  // Change tracking
  hasChanges$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** Returns the current unsaved-changes flag. */
  get hasChanges(): boolean {
    return this.hasChanges$.getValue();
  }
  /** Updates the unsaved-changes flag and notifies all subscribers. */
  set hasChanges(value: boolean) {
    this.hasChanges$.next(value);
  }

  // Grid data
  readonly hasRowsWithErrors = signal(false);
  gridData$: BehaviorSubject<DeviationSurveyGridRow[]> = new BehaviorSubject<
    DeviationSurveyGridRow[]
  >([]);

  /**
   * Formats a tooltip for a given chart point.
   */
  public tooltipFormatter(point: DeviationSurveyChartPoint): string {
    return (
      'Measured Depth: <b>' +
      point.MD +
      ' ft</b><br/>' +
      'True Vertical Depth: <b>' +
      point.TVD +
      ' ft</b><br/>' +
      (point.EW < 0 ? 'West' : 'East') +
      ': <b>' +
      Math.abs(point.EW) +
      ' ft</b><br/>' +
      (point.NS < 0 ? 'South' : 'North') +
      ': <b>' +
      Math.abs(point.NS) +
      ' ft</b><br/>' +
      'DLS: <b>' +
      point.DLS +
      ' °</b><br/>' +
      'Side Load: <b>' +
      point.SL +
      ' lbs</b>'
    );
  }

  /**
   * Recalculates chart and grid data when stroke changes.
   */
  recalculateDataOnStroke(): void {
    const gridDataValue = this.gridData$.getValue();
    if (gridDataValue.length === 0) return;

    const updatedChartInfo = this.chartInfo().slice();
    const newSteppedInfo: DeviationSurveyChartPoint[] = [];
    let initialTVD = 0;
    let currentZone = this.GREEN_ZONE_COLOR;
    let currentStep = 0;

    newSteppedInfo.push({
      MD: 0,
      TVD: 0,
      EW: 0,
      NS: 0,
      DLS: 0,
      SL: 0,
      severityColor: currentZone,
      rodColor: this.getRodColorByDepth(0),
    });

    for (let i = 1; i < gridDataValue.length; i++) {
      const gridRow = gridDataValue[i];
      const sideLoad = this.formulas.getSideLoading(gridRow.DLS, this.lastStrokePeakLoad());
      const sideLoad_fixed = Math.round(sideLoad * 100) / 100;
      gridRow.SL = sideLoad_fixed;

      const rowIndexInChart = updatedChartInfo.findIndex((point) => point.MD === gridRow.MD);
      if (rowIndexInChart !== -1) {
        updatedChartInfo[rowIndexInChart] = {
          ...updatedChartInfo[rowIndexInChart],
          SL: sideLoad_fixed,
        };
      }

      currentStep += Math.abs(gridRow.MD - gridDataValue[i - 1].MD);

      const severityZone = this.getTrajectoryZoneSeverity(
        currentZone,
        initialTVD,
        sideLoad_fixed,
        gridRow.TVD,
      );
      initialTVD = severityZone.tvd;
      currentZone = severityZone.currentZone;

      if (currentStep >= this.stepSize) {
        newSteppedInfo.push({
          MD: gridRow.MD,
          TVD: gridRow.TVD,
          EW: gridRow.EW,
          NS: gridRow.NS,
          DLS: gridRow.DLS,
          SL: sideLoad_fixed,
          severityColor: currentZone,
          rodColor: this.getRodColorByDepth(gridRow.MD),
        });
        currentStep = 0;
      }
    }

    this.maxSideLoad.set(Math.max(...updatedChartInfo.map((item) => item.SL)));
    this.chartInfo.set(updatedChartInfo);
    this.trajectoryStepSizedChartInfo.set(newSteppedInfo.reverse());
  }

  /**
   * Returns rod color for a given depth.
   */
  getRodColorByDepth(depth: number): string {
    const color = this.rodZones().find((zone) => zone.value != null && depth < zone.value);
    return color ? color.color : this.NO_RODS_COLOR;
  }

  /**
   * Determines the severity zone and TVD based on side load and limits.
   */
  getTrajectoryZoneSeverity(
    currentZone: string,
    initialTVD: number,
    sideLoad: number,
    trueVerticalDepth: number,
  ): { currentZone: string; tvd: number } {
    if (sideLoad < this.greenZoneLimit()) {
      if (currentZone !== this.GREEN_ZONE_COLOR) {
        initialTVD = trueVerticalDepth;
        currentZone = this.GREEN_ZONE_COLOR;
      }
    } else if (sideLoad < this.blueZoneLimit()) {
      if (currentZone !== this.BLUE_ZONE_COLOR) {
        initialTVD = trueVerticalDepth;
        currentZone = this.BLUE_ZONE_COLOR;
      }
    } else {
      if (currentZone !== this.RED_ZONE_COLOR) {
        initialTVD = trueVerticalDepth;
        currentZone = this.RED_ZONE_COLOR;
      }
    }
    return { currentZone, tvd: initialTVD };
  }

  /**
   * Converts a 2D array of survey data into grid rows, validates each row, and collects errors.
   * Ensures the first row starts at MD=0 if not provided.
   */
  array2Grid(data: number[][]): { rows: DeviationSurveyGridRow[]; errors: string[] } {
    this.hasRowsWithErrors.set(false);
    const gridData: DeviationSurveyGridRow[] = [];
    const errors: string[] = [];
    let rowIndex = 1;
    if (data.length && parseFloat(data[0][0].toFixed(0)) !== 0) {
      gridData.push({
        id: rowIndex,
        ROW_NUM: rowIndex,
        MD: 0,
        INC: 0,
        AZ: 0,
        NS: 0,
        EW: 0,
        TVD: 0,
        DLS: 0,
        SL: 0,
        MDHasError: false,
        INCHasError: false,
        AZHasError: false,
        rowHasError: false,
      });
      rowIndex++;
    }
    for (let i = 0; i < data.length; i++, rowIndex++) {
      const MD = parseFloat(data[i][0].toFixed(0));
      const INC = truncateDecimals(data[i][1], 2);
      const AZ = truncateDecimals(data[i][2], 2);
      const MDHasError =
        isNaN(MD) ||
        MD < DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.minAllowedDepth ||
        MD > DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.maxAllowedDepth;
      const INCHasError =
        isNaN(INC) ||
        INC < DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.minDegree ||
        INC > DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.maxIncDegree;
      const AZHasError =
        isNaN(AZ) ||
        AZ < DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.minDegree ||
        AZ > DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.maxAzDegree;
      const row: DeviationSurveyGridRow = {
        id: rowIndex,
        ROW_NUM: rowIndex,
        MD: isNaN(MD) ? NaN : MD,
        INC: isNaN(INC) ? NaN : INC,
        AZ: isNaN(AZ) ? NaN : AZ,
        NS: NaN,
        EW: NaN,
        TVD: NaN,
        DLS: NaN,
        SL: NaN,
        MDHasError,
        INCHasError,
        AZHasError,
        rowHasError: MDHasError || INCHasError || AZHasError,
      };
      gridData.push(row);
      if (row.rowHasError) {
        this.hasRowsWithErrors.set(true);
        errors.push(`Error at row ${row.ROW_NUM}`);
      }
    }
    return { rows: gridData, errors };
  }

  /**
   * Computes coordinates, DLS, side load, and severity zones for all grid rows, then updates all signals.
   */
  calculateData(data: DeviationSurveyGridRow[]): void {
    this.gridData$.next(data);
    const chartInfo: DeviationSurveyChartPoint[] = [];
    const stepSizedChartInfo: DeviationSurveyChartPoint[] = [];
    let currentZone = this.GREEN_ZONE_COLOR;
    let initialTVD = 0;
    let currentStep = 0;
    const gridDataValue = this.gridData$.getValue();
    const chartResolutionModule = Math.ceil(gridDataValue.length / this.MAX_CHART_POINTS);
    let eastWest = 0.0;
    let northSouth = 0.0;
    let trueVertical = 0.0;
    let beta = 0.0;
    let dogLegSeverity = 0.0;
    let sideLoad = 0.0;
    if (gridDataValue.length === 0) {
      this.chartInfo.set([]);
      this.trajectoryChartInfo.set([]);
      this.trajectoryStepSizedChartInfo.set([]);
      this.maxSideLoad.set(0);
      return;
    }
    gridDataValue[0].NS = northSouth;
    gridDataValue[0].EW = eastWest;
    gridDataValue[0].TVD = trueVertical;
    gridDataValue[0].DLS = dogLegSeverity;
    gridDataValue[0].SL = sideLoad;
    chartInfo.push({
      MD: 0,
      TVD: trueVertical,
      EW: eastWest,
      NS: northSouth,
      DLS: dogLegSeverity,
      SL: sideLoad,
      severityColor: this.GREEN_ZONE_COLOR,
      rodColor: this.getRodColorByDepth(0),
    });
    for (let i = 1; i < gridDataValue.length; i++) {
      const prev = gridDataValue[i - 1];
      const curr = gridDataValue[i];
      const MD1 = -prev.MD;
      const INC1 = this.formulas.toRadians(prev.INC);
      const AZ1 = this.formulas.toRadians(prev.AZ);
      const MD2 = -curr.MD;
      const INC2 = this.formulas.toRadians(curr.INC);
      const AZ2 = this.formulas.toRadians(curr.AZ);
      currentStep += Math.abs(MD2 - MD1);
      beta = this.formulas.getDogLegAngle(INC1, AZ1, INC2, AZ2);
      const RF = this.formulas.getTrajectoryPath(beta);
      eastWest = this.formulas.getEastWestCoordinate(MD1, MD2, INC1, INC2, AZ1, AZ2, RF, eastWest);
      northSouth = this.formulas.getNorthSouthCoordinate(
        MD1,
        MD2,
        INC1,
        INC2,
        AZ1,
        AZ2,
        RF,
        northSouth,
      );
      trueVertical = this.formulas.getTrueVerticalCoordinate(
        MD1,
        MD2,
        INC1,
        INC2,
        RF,
        trueVertical,
      );
      dogLegSeverity = this.formulas.getDogLegSeverity(beta, MD1, MD2);
      sideLoad = this.formulas.getSideLoading(dogLegSeverity, this.lastStrokePeakLoad());
      curr.NS = Math.round(northSouth * 100) / 100;
      curr.EW = Math.round(eastWest * 100) / 100;
      curr.TVD = Math.round(trueVertical * 100) / 100;
      curr.DLS = Math.round(dogLegSeverity * 100) / 100;
      curr.SL = Math.round(sideLoad * 100) / 100;
      const severityZone = this.getTrajectoryZoneSeverity(
        currentZone,
        initialTVD,
        curr.SL,
        curr.TVD,
      );
      initialTVD = severityZone.tvd;
      currentZone = severityZone.currentZone;
      if (isNaN(eastWest) || isNaN(northSouth) || isNaN(trueVertical)) continue;
      if (i % chartResolutionModule === 0 || i === gridDataValue.length - 1) {
        chartInfo.push({
          MD: -MD2,
          TVD: curr.TVD,
          EW: curr.EW,
          NS: curr.NS,
          DLS: curr.DLS,
          SL: curr.SL,
          severityColor: currentZone,
          rodColor: this.getRodColorByDepth(-MD2),
        });
      }
      if (currentStep >= this.stepSize) {
        stepSizedChartInfo.push({
          MD: -MD2,
          TVD: curr.TVD,
          EW: curr.EW,
          NS: curr.NS,
          DLS: curr.DLS,
          SL: curr.SL,
          severityColor: currentZone,
          rodColor: this.getRodColorByDepth(-MD2),
        });
        currentStep = 0;
      }
    }
    const sideLoadValues = chartInfo.map((item) => item.SL);
    this.maxSideLoad.set(sideLoadValues.length > 0 ? Math.max(...sideLoadValues) : 0);
    this.chartInfo.set(chartInfo);
    this.trajectoryChartInfo.set(chartInfo);
    this.trajectoryStepSizedChartInfo.set(stepSizedChartInfo.reverse());
  }

  /**
   * Full pipeline: validates raw survey data, computes all derived values, and updates all signals.
   * Returns validation error messages for the caller to display.
   */
  processAndLoadData(data: number[][]): string[] {
    const { rows, errors } = this.array2Grid(data);
    this.calculateData(rows);
    return errors;
  }

  /**
   * Handles a file input change event: validates type/size, reads the file, and loads it.
   */
  public readCsvFile(event: Event, onDone?: () => void): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      this.toastr.warning('No file selected');
      return;
    }
    const isCsvOrTxt =
      file.type === 'text/csv' ||
      file.type === 'text/plain' ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.txt');
    if (!isCsvOrTxt) {
      this.toastr.error('Upload a valid CSV or TXT file.');
      onDone?.();
      return;
    }
    if (file.size > DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.maxFileSizeInBytes) {
      this.toastr.error(
        `The selected file exceeds the maximum size of ${
          DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.maxFileSizeInBytes / (1024 * 1024)
        } MB.`,
      );
      onDone?.();
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.parseCsvData(e.target?.result as string);
    };
    reader.readAsText(file);
    onDone?.();
  }

  /** Parses raw CSV text, validates row/size limits, and loads the resulting data into the service. */
  private parseCsvData(csvContent: string): void {
    if (csvContent.trim() === '') {
      this.toastr.warning('The uploaded file is empty.');
      return;
    }
    const lines = csvContent.split(/\r\n|\r|\n/);
    const dataRaw = lines.map((line) => line.split(','));
    if (dataRaw.length > DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.maxAllowedRows) {
      this.toastr.error(
        `The uploaded file exceeds the maximum of ${DEVIATION_SURVEY_CONSTANTS.VALIDATIONS.maxAllowedRows} rows.`,
      );
      return;
    }
    const data = this.csv2Array(dataRaw);
    if (data.length === 0) {
      this.toastr.warning('No valid data found in the CSV file.');
      return;
    }
    const errors = this.processAndLoadData(data);
    this.hasChanges = true;
    this.displayCsvErrors(errors);
  }

  /** Converts a 2-D string array of CSV rows into a numeric [MD, INC, AZ] tuple array, skipping all-NaN rows. */
  private csv2Array(csvData: string[][]): number[][] {
    const data: number[][] = [];
    for (const row of csvData) {
      const MD = row[0] ? parseFloat(row[0]) : NaN;
      const INC = row[1] ? parseFloat(row[1]) : NaN;
      const AZ = row[2] ? parseFloat(row[2]) : NaN;
      if (isNaN(MD) && isNaN(INC) && isNaN(AZ)) continue;
      data.push([MD, INC, AZ]);
    }
    return data;
  }

  /** Displays CSV validation errors as toastr warnings, truncating to the first 5 when there are many. */
  private displayCsvErrors(errors: string[]): void {
    if (errors.length === 0) return;
    if (errors.length <= 5) {
      this.toastr.warning(errors.join('<br/>'), '', { enableHtml: true });
    } else {
      const first5 = errors.slice(0, 5).join('<br/>');
      const label = errors.length - 5 === 1 ? 'error' : 'errors';
      this.toastr.warning(`${first5}<br/>... + ${errors.length - 5} more ${label} found.`, '', {
        enableHtml: true,
        timeOut: 3000,
      });
    }
  }

  /**
   * Downloads the current survey data as a CSV file.
   */
  public downloadCurrentSurveyData(wellDisplayName: string): void {
    let csvContent =
      this.columnNames.MD +
      ',' +
      this.columnNames.INC +
      ',' +
      this.columnNames.AZ +
      ',' +
      this.columnNames.NS +
      ',' +
      this.columnNames.EW +
      ',' +
      this.columnNames.TVD +
      ',' +
      this.columnNames.DLS +
      ',' +
      this.columnNames.SL +
      '\n';

    this.gridData$.getValue().forEach((row) => {
      const dataToExport = [row.MD, row.INC, row.AZ, row.NS, row.EW, row.TVD, row.DLS, row.SL];
      csvContent += dataToExport.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileUrl = URL.createObjectURL(blob);
    link.setAttribute('href', fileUrl);
    link.setAttribute('download', wellDisplayName + DEVIATION_SURVEY_CONSTANTS.FILE_SUFFIX);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(fileUrl);
  }
}
